import { Navigate } from "react-router-dom";
import { hasAtLeast, type HubPermissionKey, type Role } from "@wwrp/shared";
import { useAuth } from "../contexts/AuthContext.js";
import { getAccessRole } from "../api/auth.js";
import type { ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Minimum role required. Defaults to any authenticated user. */
  minRole?: Role;
  /**
   * When set, access is decided by the resolved permissions map from /auth/me
   * rather than a static role comparison. Takes precedence over `minRole`.
   */
  permissionKey?: HubPermissionKey;
  /** Require the private sysadmin grant rather than a public community role. */
  sysadminOnly?: boolean;
}

export function ProtectedRoute({ children, minRole, permissionKey, sysadminOnly }: Props) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <span className="spinner" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (sysadminOnly && (!user.isSysadmin || user.viewingAsRole)) {
    return <Navigate to="/" replace />;
  }

  if (permissionKey) {
    if (!(user.permissions?.[permissionKey] ?? false)) return <Navigate to="/" replace />;
  } else if (minRole && !hasAtLeast(getAccessRole(user)!, minRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
